import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Save, Eye, EyeOff, Mail, MessageCircle, Bell, Search, Send,
  CheckCircle, XCircle, CreditCard, Map, Smartphone, Target, Shield
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get admin auth headers
const getAdminHeaders = () => {
  const token = localStorage.getItem('shootzu_admin_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const AdminSettings = () => {
  const navigate = useNavigate();
  const [stripeMode, setStripeMode] = useState('test');
  const [showKeys, setShowKeys] = useState({
    stripeTestPub: false,
    stripeTestSecret: false,
    stripeLivePub: false,
    stripeLiveSecret: false,
    awsAccessKey: false,
    awsSecretKey: false,
    googleMapsKey: false,
    smsGlobalKey: false,
    smsGlobalSecret: false
  });

  const [stripeKeys, setStripeKeys] = useState({
    testPublishable: '',
    testSecret: '',
    livePublishable: '',
    liveSecret: ''
  });

  const [sesKeys, setSesKeys] = useState({
    region: 'us-east-1',
    accessKeyId: '',
    secretAccessKey: '',
    verifiedEmail: 'support@shootzu.com'
  });

  const [googleMapsKey, setGoogleMapsKey] = useState('');
  const [supportEnabled, setSupportEnabled] = useState(true);
  const [leadSettings, setLeadSettings] = useState({
    leadRevealPrice: 50,
    smsGlobalSender: 'Shootzu'
  });
  const [smsGlobal, setSmsGlobal] = useState({ sender: 'Shootzu', apiKey: '', apiSecret: '' });

  // Push notification testing state
  const [pushTestSearch, setPushTestSearch] = useState('');
  const [pushTestUsers, setPushTestUsers] = useState([]);
  const [pushTestSelectedUser, setPushTestSelectedUser] = useState(null);
  const [pushTestMessage, setPushTestMessage] = useState('This is a notification test');
  const [pushTestLoading, setPushTestLoading] = useState(false);
  const [pushTestSearching, setPushTestSearching] = useState(false);
  const [pushTestResult, setPushTestResult] = useState(null);

  useEffect(() => {
    const isAdmin = localStorage.getItem('shootzu_admin');
    if (!isAdmin) navigate('/szadmin');

    // Load existing settings
    loadSettings();
  }, [navigate]);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, { headers: getAdminHeaders() });
      const { stripe, ses, stripeMode: mode, googleMaps, support_enabled, lead_reveal_price, sms_global_sender, smsGlobal: smsGlobalData } = response.data;

      if (stripe) {
        setStripeKeys({
          testPublishable: stripe.testPublishable || '',
          testSecret: stripe.testSecret || '',
          livePublishable: stripe.livePublishable || '',
          liveSecret: stripe.liveSecret || ''
        });
      }

      if (ses) {
        setSesKeys({
          region: ses.region || 'us-east-1',
          accessKeyId: ses.accessKeyId || '',
          secretAccessKey: ses.secretAccessKey || '',
          verifiedEmail: ses.verifiedEmail || 'support@shootzu.com'
        });
      }

      if (googleMaps) {
        setGoogleMapsKey(googleMaps.apiKey || '');
      }

      if (mode) {
        setStripeMode(mode);
      }

      // Load support enabled setting
      setSupportEnabled(support_enabled !== undefined ? support_enabled : true);

      // Load lead settings
      setLeadSettings({
        leadRevealPrice: lead_reveal_price || 50,
        smsGlobalSender: sms_global_sender || 'Shootzu'
      });
      if (smsGlobalData) { setSmsGlobal({ sender: smsGlobalData.sender || 'Shootzu', apiKey: smsGlobalData.apiKey || '', apiSecret: smsGlobalData.apiSecret || '' }); }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleSaveStripe = async () => {
    try {
      await axios.post(`${API}/admin/settings/stripe`, {
        ...stripeKeys,
        mode: stripeMode
      }, { headers: getAdminHeaders() });
      toast.success('Stripe settings saved successfully');
    } catch (error) {
      toast.error('Failed to save Stripe settings');
    }
  };

  const handleSaveSES = async () => {
    try {
      await axios.post(`${API}/admin/settings/ses`, sesKeys, { headers: getAdminHeaders() });
      toast.success('Amazon SES settings saved successfully');
    } catch (error) {
      toast.error('Failed to save SES settings');
    }
  };

  const handleSaveGoogleMaps = async () => {
    try {
      await axios.post(`${API}/admin/settings/googlemaps`, {
        apiKey: googleMapsKey
      }, { headers: getAdminHeaders() });
      toast.success('Google Maps API Key saved successfully');
    } catch (error) {
      toast.error('Failed to save Google Maps settings');
    }
  };

  const handleSaveSupport = async () => {
    try {
      await axios.post(`${API}/admin/settings/support`, {
        supportEnabled: supportEnabled
      }, { headers: getAdminHeaders() });
      toast.success(`Support menu ${supportEnabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      toast.error('Failed to save support settings');
    }
  };


  const handleSaveLeads = async () => {
    try {
      await axios.post(`${API}/admin/settings/leads`, leadSettings, { headers: getAdminHeaders() });
      toast.success('Lead settings saved successfully');
    } catch (error) {
      toast.error('Failed to save lead settings');
    }
  };

  const handleSaveSmsGlobal = async () => {
    try {
      await axios.post(`${API}/admin/settings/smsglobal`, smsGlobal, { headers: getAdminHeaders() });
      toast.success("SMS Global settings saved successfully");
    } catch (error) {
      toast.error("Failed to save SMS Global settings");
    }
  };

  // Debounced user search for push notifications
  const searchUsersForPush = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setPushTestUsers([]);
      return;
    }

    setPushTestSearching(true);
    try {
      const response = await axios.get(`${API}/admin/users/search?query=${encodeURIComponent(query)}&limit=10`, { headers: getAdminHeaders() });
      setPushTestUsers(response.data.users || []);
    } catch (error) {
      console.error('User search failed:', error);
      setPushTestUsers([]);
    } finally {
      setPushTestSearching(false);
    }
  }, []);

  // Debounce search input
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      searchUsersForPush(pushTestSearch);
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [pushTestSearch, searchUsersForPush]);

  const handleSendTestPush = async () => {
    if (!pushTestSelectedUser) {
      toast.error('Please select a user first');
      return;
    }

    setPushTestLoading(true);
    setPushTestResult(null);

    try {
      const response = await axios.post(`${API}/admin/test-push-notification`, {
        user_email: pushTestSelectedUser.email,
        message: pushTestMessage
      }, { headers: getAdminHeaders() });

      setPushTestResult(response.data);

      if (response.data.status === 'success') {
        toast.success(`Test notification sent to ${pushTestSelectedUser.name}`);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to send test notification';
      toast.error(errorMessage);
      setPushTestResult({ status: 'error', message: errorMessage });
    } finally {
      setPushTestLoading(false);
    }
  };

  const maskKey = (key, show) => {
    if (!key) return '';
    if (show) return key;
    if (key.length < 8) return '•'.repeat(key.length);
    return key.substring(0, 7) + '•'.repeat(key.length - 7);
  };

  const toggleKeyVisibility = (keyName) => {
    setShowKeys(prev => ({ ...prev, [keyName]: !prev[keyName] }));
  };

  // ---- presentational helpers (SZadmin design) ----
  const cardCls = "sz-card sz-fade overflow-hidden";

  const SectionHead = ({ icon: Icon, chipBg, chipFg, title, sub, right }) => (
    <div className="flex items-center gap-3 px-5 py-4 border-b border-[#F1F3F7] flex-wrap">
      <div
        className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
        style={{ background: chipBg, color: chipFg }}
      >
        <Icon className="w-[18px] h-[18px]" />
      </div>
      <div className="flex-1 min-w-[120px]">
        <h3 className="sz-h3 m-0">{title}</h3>
        <div className="text-[11.5px] text-[#8A90A2] mt-0.5">{sub}</div>
      </div>
      {right}
    </div>
  );

  const FieldLabel = ({ children, htmlFor }) => (
    <label htmlFor={htmlFor} className="block text-[12px] font-semibold text-[#2B3142] mb-1.5">
      {children}
    </label>
  );

  // Masked secret input with eye toggle
  const SecretField = ({ label, value, onChange, placeholder, show, onToggle, warn = false }) => (
    <div className="mb-3.5">
      <FieldLabel>{label}</FieldLabel>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`w-full pl-3 pr-10 py-2.5 rounded-[10px] text-[13px] outline-none transition-colors focus:border-[#28C0D0] ${
            warn ? 'border border-[#FCA5A5] bg-[#FEF2F2]' : 'border border-[#E0E3EB] bg-white'
          }`}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-[26px] h-[26px] rounded-[7px] bg-transparent text-[#A8AEBC] hover:text-[#2B3142] flex items-center justify-center"
        >
          {show ? <EyeOff className="w-[15px] h-[15px]" /> : <Eye className="w-[15px] h-[15px]" />}
        </button>
      </div>
    </div>
  );

  // Plain (non-secret) text input
  const TextField = ({ label, value, onChange, placeholder, type = "text", maxLength, id, step, hint }) => (
    <div className="mb-3.5">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <input
        id={id}
        type={type}
        step={step}
        maxLength={maxLength}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 rounded-[10px] text-[13px] outline-none border border-[#E0E3EB] bg-white transition-colors focus:border-[#28C0D0]"
      />
      {hint && <p className="text-[11px] text-[#8A90A2] mt-1">{hint}</p>}
    </div>
  );

  const SaveBtn = ({ onClick, children, disabled }) => (
    <div className="flex justify-end mt-1">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="sz-btn sz-btn-primary disabled:opacity-50"
      >
        <Save className="w-4 h-4" />
        {children}
      </button>
    </div>
  );

  return (
    <>
      {/* Sticky page header */}
      <header className="sz-header flex items-center justify-between gap-[18px]">
        <div>
          <h1 className="sz-h1 m-0">Integration Settings</h1>
          <div className="text-[12.5px] text-[#8A90A2] mt-[3px]">API keys &amp; third-party services</div>
        </div>
      </header>

      {/* Scroll body */}
      <main
        className="flex flex-col gap-4"
        style={{ padding: "22px 30px 56px", maxWidth: 760 }}
      >
        {/* ---- Stripe ---- */}
        <div className={cardCls}>
          <SectionHead
            icon={CreditCard}
            chipBg="#EDE4F4"
            chipFg="#78287C"
            title="Stripe"
            sub="Payments & payouts"
            right={
              <div className="inline-flex items-center gap-1 bg-[#F5F6F9] p-0.5 rounded-[10px] border border-[#E6E9EF]">
                <button
                  type="button"
                  onClick={() => setStripeMode('test')}
                  className={`px-3 py-1.5 rounded-[8px] text-[12px] font-semibold transition-all ${
                    stripeMode === 'test' ? 'bg-[#0C1120] text-white' : 'text-[#8A90A2] hover:text-[#0C1120]'
                  }`}
                >
                  Test
                </button>
                <button
                  type="button"
                  onClick={() => setStripeMode('live')}
                  className={`px-3 py-1.5 rounded-[8px] text-[12px] font-semibold transition-all ${
                    stripeMode === 'live' ? 'bg-[#DC2626] text-white' : 'text-[#8A90A2] hover:text-[#0C1120]'
                  }`}
                >
                  Live
                </button>
              </div>
            }
          />
          <div className="px-5 py-[18px]">
            {stripeMode === 'test' && (
              <>
                <SecretField
                  label="Publishable key (test)"
                  value={maskKey(stripeKeys.testPublishable, showKeys.stripeTestPub)}
                  onChange={(e) => setStripeKeys({ ...stripeKeys, testPublishable: e.target.value })}
                  placeholder="pk_test_..."
                  show={showKeys.stripeTestPub}
                  onToggle={() => toggleKeyVisibility('stripeTestPub')}
                />
                <SecretField
                  label="Secret key (test)"
                  value={maskKey(stripeKeys.testSecret, showKeys.stripeTestSecret)}
                  onChange={(e) => setStripeKeys({ ...stripeKeys, testSecret: e.target.value })}
                  placeholder="sk_test_..."
                  show={showKeys.stripeTestSecret}
                  onToggle={() => toggleKeyVisibility('stripeTestSecret')}
                />
              </>
            )}
            {stripeMode === 'live' && (
              <>
                <SecretField
                  label="Publishable key (live)"
                  value={maskKey(stripeKeys.livePublishable, showKeys.stripeLivePub)}
                  onChange={(e) => setStripeKeys({ ...stripeKeys, livePublishable: e.target.value })}
                  placeholder="pk_live_..."
                  show={showKeys.stripeLivePub}
                  onToggle={() => toggleKeyVisibility('stripeLivePub')}
                  warn
                />
                <SecretField
                  label="Secret key (live)"
                  value={maskKey(stripeKeys.liveSecret, showKeys.stripeLiveSecret)}
                  onChange={(e) => setStripeKeys({ ...stripeKeys, liveSecret: e.target.value })}
                  placeholder="sk_live_..."
                  show={showKeys.stripeLiveSecret}
                  onToggle={() => toggleKeyVisibility('stripeLiveSecret')}
                  warn
                />
              </>
            )}
            <SaveBtn onClick={handleSaveStripe}>Save Stripe</SaveBtn>
          </div>
        </div>

        {/* ---- Amazon SES ---- */}
        <div className={cardCls}>
          <SectionHead
            icon={Mail}
            chipBg="#FEF3C7"
            chipFg="#B45309"
            title="Amazon SES"
            sub="Transactional email"
          />
          <div className="px-5 py-[18px]">
            <div className="mb-3.5">
              <FieldLabel>Region</FieldLabel>
              <select
                value={sesKeys.region}
                onChange={(e) => setSesKeys({ ...sesKeys, region: e.target.value })}
                className="w-full px-3 py-2.5 rounded-[10px] text-[13px] outline-none border border-[#E0E3EB] bg-white transition-colors focus:border-[#28C0D0]"
              >
                <option value="us-east-1">US East (N. Virginia)</option>
                <option value="us-west-2">US West (Oregon)</option>
                <option value="eu-west-1">EU (Ireland)</option>
                <option value="ap-southeast-2">Asia Pacific (Sydney)</option>
              </select>
            </div>
            <SecretField
              label="Access key ID"
              value={maskKey(sesKeys.accessKeyId, showKeys.awsAccessKey)}
              onChange={(e) => setSesKeys({ ...sesKeys, accessKeyId: e.target.value })}
              placeholder="AKIA..."
              show={showKeys.awsAccessKey}
              onToggle={() => toggleKeyVisibility('awsAccessKey')}
            />
            <SecretField
              label="Secret access key"
              value={maskKey(sesKeys.secretAccessKey, showKeys.awsSecretKey)}
              onChange={(e) => setSesKeys({ ...sesKeys, secretAccessKey: e.target.value })}
              placeholder="aws_secret_..."
              show={showKeys.awsSecretKey}
              onToggle={() => toggleKeyVisibility('awsSecretKey')}
            />
            <TextField
              label="Verified sender"
              type="email"
              value={sesKeys.verifiedEmail}
              onChange={(e) => setSesKeys({ ...sesKeys, verifiedEmail: e.target.value })}
              placeholder="support@shootzu.com"
              hint="This email must be verified in AWS SES console"
            />
            <SaveBtn onClick={handleSaveSES}>Save SES</SaveBtn>
          </div>
        </div>

        {/* ---- Google Maps API ---- */}
        <div className={cardCls}>
          <SectionHead
            icon={Map}
            chipBg="#DBEAFE"
            chipFg="#2563EB"
            title="Google Maps API"
            sub="Geocoding & maps"
          />
          <div className="px-5 py-[18px]">
            <SecretField
              label="API key"
              value={googleMapsKey}
              onChange={(e) => setGoogleMapsKey(e.target.value)}
              placeholder="AIzaSy..."
              show={showKeys.googleMapsKey}
              onToggle={() => setShowKeys({ ...showKeys, googleMapsKey: !showKeys.googleMapsKey })}
            />
            <p className="text-[11px] text-[#8A90A2] -mt-1.5 mb-3.5">
              Get your API key from{" "}
              <a
                href="https://console.cloud.google.com/apis/credentials"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2563EB] hover:underline"
              >
                Google Cloud Console
              </a>
              . Enable: Maps JavaScript, Places, Geocoding & Distance Matrix APIs.
            </p>
            <SaveBtn onClick={handleSaveGoogleMaps}>Save Maps</SaveBtn>
          </div>
        </div>

        {/* ---- Live Chat Support (toggle) ---- */}
        <div className={cardCls}>
          <SectionHead
            icon={MessageCircle}
            chipBg="#CCFBF1"
            chipFg="#0F766E"
            title="Live Chat Support"
            sub="In-app support widget"
            right={
              <button
                type="button"
                onClick={() => setSupportEnabled(!supportEnabled)}
                aria-pressed={supportEnabled}
                className="relative w-[42px] h-[24px] rounded-full flex-shrink-0 transition-colors"
                style={{ background: supportEnabled ? '#15803D' : '#CBD0DA' }}
              >
                <span
                  className="absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white transition-all"
                  style={{ left: supportEnabled ? '21px' : '3px', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }}
                />
              </button>
            }
          />
          <div className="px-5 py-[18px]">
            <p className="text-[12.5px] leading-[1.55] text-[#2B3142] mb-3.5">
              Show the "Support" menu item in the user sidebar to access live chat. Powered by Turbo Chat,
              auto-populates the user's name and email, and is available to logged-in users only.
            </p>
            <SaveBtn onClick={handleSaveSupport}>Save Chat</SaveBtn>
          </div>
        </div>

        {/* ---- SMS Global ---- */}
        <div className={cardCls}>
          <SectionHead
            icon={Smartphone}
            chipBg="#E0E7FF"
            chipFg="#4338CA"
            title="SMS Global"
            sub="Phone verification"
          />
          <div className="px-5 py-[18px]">
            <TextField
              label="Sender name (max 11 chars)"
              value={smsGlobal.sender}
              onChange={(e) => setSmsGlobal(prev => ({ ...prev, sender: e.target.value }))}
              placeholder="Shootzu"
              maxLength={11}
            />
            <SecretField
              label="API key"
              value={smsGlobal.apiKey}
              onChange={(e) => setSmsGlobal(prev => ({ ...prev, apiKey: e.target.value }))}
              placeholder="Enter API Key"
              show={showKeys.smsGlobalKey}
              onToggle={() => setShowKeys(prev => ({ ...prev, smsGlobalKey: !prev.smsGlobalKey }))}
            />
            <SecretField
              label="API secret"
              value={smsGlobal.apiSecret}
              onChange={(e) => setSmsGlobal(prev => ({ ...prev, apiSecret: e.target.value }))}
              placeholder="Enter API Secret"
              show={showKeys.smsGlobalSecret}
              onToggle={() => setShowKeys(prev => ({ ...prev, smsGlobalSecret: !prev.smsGlobalSecret }))}
            />
            <SaveBtn onClick={handleSaveSmsGlobal}>Save SMS</SaveBtn>
          </div>
        </div>

        {/* ---- Lead System ---- */}
        <div className={cardCls}>
          <SectionHead
            icon={Target}
            chipBg="#CFFAFE"
            chipFg="#0E7490"
            title="Lead System"
            sub="Reveal pricing"
          />
          <div className="px-5 py-[18px]">
            <TextField
              id="leadPrice"
              label="Lead reveal price (AUD)"
              type="number"
              step="0.01"
              value={leadSettings.leadRevealPrice}
              onChange={(e) => setLeadSettings(prev => ({ ...prev, leadRevealPrice: parseFloat(e.target.value) }))}
              placeholder="50.00"
              hint="Price charged to reveal lead contact details (GST added automatically)"
            />
            <TextField
              id="smsSender"
              label="SMS sender name"
              value={leadSettings.smsGlobalSender}
              onChange={(e) => setLeadSettings(prev => ({ ...prev, smsGlobalSender: e.target.value }))}
              placeholder="Shootzu"
              maxLength={11}
              hint="Sender name displayed in OTP SMS messages (max 11 characters)"
            />
            <SaveBtn onClick={handleSaveLeads}>Save Leads</SaveBtn>
          </div>
        </div>

        {/* ---- Test Push Notification ---- */}
        <div className={cardCls}>
          <SectionHead
            icon={Bell}
            chipBg="#FCE7F3"
            chipFg="#BE185D"
            title="Test Push Notification"
            sub="Send test notifications to mobile app users"
          />
          <div className="px-5 py-[18px]">
            {/* User Search */}
            <div className="mb-3.5">
              <FieldLabel>Search user</FieldLabel>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8AEBC]" />
                <input
                  value={pushTestSearch}
                  onChange={(e) => {
                    setPushTestSearch(e.target.value);
                    setPushTestSelectedUser(null);
                    setPushTestResult(null);
                  }}
                  placeholder="Search by email or name..."
                  className="w-full pl-10 pr-3 py-2.5 rounded-[10px] text-[13px] outline-none border border-[#E0E3EB] bg-white transition-colors focus:border-[#28C0D0]"
                />
                {pushTestSearching && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-[#A8AEBC]">
                    Searching...
                  </span>
                )}
              </div>

              {/* Search Results Dropdown */}
              {pushTestUsers.length > 0 && !pushTestSelectedUser && (
                <div className="sz-menu mt-2 max-h-48 overflow-y-auto szscroll">
                  {pushTestUsers.map((user) => (
                    <button
                      key={user.email}
                      onClick={() => {
                        setPushTestSelectedUser(user);
                        setPushTestSearch('');
                        setPushTestUsers([]);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-[#FAFBFC] flex items-center justify-between border-b border-[#F1F3F7] last:border-b-0"
                    >
                      <div>
                        <p className="font-medium text-[#0C1120] text-[13px]">{user.name}</p>
                        <p className="text-[12px] text-[#8A90A2]">{user.email}</p>
                      </div>
                      {user.has_push_token ? (
                        <span className="sz-badge sz-nodot text-[#15803D] bg-[#DCFCE7]">
                          <CheckCircle className="w-3 h-3" />
                          Has Token
                        </span>
                      ) : (
                        <span className="sz-badge sz-nodot text-[#DC2626] bg-[#FEE2E2]">
                          <XCircle className="w-3 h-3" />
                          No Token
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected User Display */}
            {pushTestSelectedUser && (
              <div className="mb-3.5 p-4 rounded-[12px] border border-[#FBCFE8] bg-[#FDF2F8]">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="font-medium text-[#0C1120] text-[13px]">{pushTestSelectedUser.name}</p>
                    <p className="text-[12px] text-[#8A90A2]">{pushTestSelectedUser.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {pushTestSelectedUser.has_push_token ? (
                      <span className="sz-badge sz-nodot text-[#15803D] bg-[#DCFCE7]">
                        <CheckCircle className="w-3 h-3" />
                        Push Token Active
                      </span>
                    ) : (
                      <span className="sz-badge sz-nodot text-[#DC2626] bg-[#FEE2E2]">
                        <XCircle className="w-3 h-3" />
                        No Push Token
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setPushTestSelectedUser(null);
                        setPushTestResult(null);
                      }}
                      className="text-[12px] font-semibold text-[#8A90A2] hover:text-[#0C1120]"
                    >
                      Change
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Custom Message */}
            <TextField
              label="Notification message"
              value={pushTestMessage}
              onChange={(e) => setPushTestMessage(e.target.value)}
              placeholder="This is a notification test"
              maxLength={200}
              hint={`${pushTestMessage.length}/200 characters`}
            />

            {/* Result Display */}
            {pushTestResult && (
              <div
                className={`mb-3.5 p-4 rounded-[12px] border ${
                  pushTestResult.status === 'success'
                    ? 'bg-[#F0FDF4] border-[#BBF7D0]'
                    : 'bg-[#FEF2F2] border-[#FECACA]'
                }`}
              >
                <div className="flex items-start gap-3">
                  {pushTestResult.status === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-[#16A34A] mt-0.5 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-5 h-5 text-[#DC2626] mt-0.5 flex-shrink-0" />
                  )}
                  <div>
                    <p className={`font-semibold text-[13px] ${pushTestResult.status === 'success' ? 'text-[#166534]' : 'text-[#991B1B]'}`}>
                      {pushTestResult.status === 'success' ? 'Notification Sent!' : 'Failed to Send'}
                    </p>
                    <p className={`text-[12px] ${pushTestResult.status === 'success' ? 'text-[#15803D]' : 'text-[#B91C1C]'}`}>
                      {pushTestResult.message}
                    </p>
                    {pushTestResult.push_token_preview && (
                      <p className="text-[11px] text-[#8A90A2] mt-1 sz-mono">
                        Token: {pushTestResult.push_token_preview}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end mt-1">
              <button
                type="button"
                onClick={handleSendTestPush}
                disabled={!pushTestSelectedUser || pushTestLoading || !pushTestSelectedUser?.has_push_token}
                className="sz-btn sz-btn-primary disabled:opacity-50"
              >
                {pushTestLoading ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Test Notification
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ---- Security notice ---- */}
        <div className="flex items-start gap-3 px-4 py-3.5 rounded-[14px] bg-[#EFF6FF] border border-[#BFDBFE]">
          <Shield className="w-[18px] h-[18px] text-[#2563EB] flex-shrink-0 mt-0.5" />
          <div className="text-[12.5px] leading-[1.55] text-[#1E40AF]">
            Secrets are encrypted at rest and masked in the UI. Only the last 4 characters are ever shown.
            Rotating a key takes effect immediately for all new requests.
          </div>
        </div>
      </main>
    </>
  );
};

export default AdminSettings;
